<script lang="ts" context="module">
  export let loadingRaces = writable<boolean>(false);

  const _scrollToRace = writable(async (raceId: string) => {
    console.log('scrollToRace not implemented', { raceId });
  });
  export const scrollToActiveRace = derived(
    [_scrollToRace, selectedRaceId],
    ([$scrollToRace, $selectedRaceId]) => {
      return async () => {
        if ($selectedRaceId) {
          return await $scrollToRace($selectedRaceId);
        }
        return () => null;
      };
    }
  ) as Readable<() => Promise<void>>;
</script>

<script lang="ts">
  import Button from '$components/Button.svelte';
  import BarLoader from '$components/spinners/BarLoader.svelte';
  import ScaleOut from '$components/spinners/ScaleOut.svelte';
  import TinyVirtualList from '$components/virtual-list/TinyVirtualList.svelte';
  import type { ScrollBehavior } from '$components/virtual-list/types';
  import type { Race } from '$types/races';
  import { isFiltering, raceFilters } from '$utils/filters';
  import { selectedRaceId } from '$utils/navigation';
  import { sidebarOpen } from '$utils/ui';
  import { onMount } from 'svelte';
  import { derived, writable, type Readable } from 'svelte/store';
  import TablerAlertSquareRounded from '~icons/tabler/alert-square-rounded';
  import RaceListHeader from './RaceListHeader.svelte';
  import RaceListItem from './RaceListItem.svelte';
  import { isRaceClosed } from '$utils/race';

  export let races: Race[];

  type RaceListHeaderType = {
    type: 'HEADER';
    date: string;
  };

  type RaceListType = {
    type: 'ITEM';
    race: Race;
  };

  let divRaceList: HTMLElement;
  let raceListDivs = {} as { [x: string]: HTMLElement };

  $: items = [] as (RaceListType | RaceListHeaderType)[];

  // TODO this recreates on every nav #perf
  $: {
    items = [];
    let lastDate = '';
    races.forEach((r) => {
      const date = r.startTime.toDateString();
      if (date !== lastDate) {
        items.push({ type: 'HEADER', date });
        lastDate = date;
      }
      items.push({ type: 'ITEM', race: r });
    });
  }

  export function scrollIntoView(behavior: 'smooth' | 'auto' = 'auto') {
    const div = $selectedRaceId ? raceListDivs[$selectedRaceId] : null;

    if (!div) return;
    // scroll div into view
    divRaceList.scrollTo({
      top: div.offsetTop - 250,
      behavior: behavior
    });
  }

  // scrolling
  onMount(async () => {
    if ($selectedRaceId) {
      setTimeout(async () => {
        if (
          $selectedRaceId &&
          races.map((r) => r.id).includes($selectedRaceId)
        ) {
          await scrollToRace($selectedRaceId);
        } else {
          // scroll to next open race
          const nextOpenRace = races.find((r) => isRaceClosed(r) === false);
          if (nextOpenRace) {
            await scrollToRace(nextOpenRace.id);
          }
        }
      }, 100);
    }
  });
  let scrollToIndex: (index: number, behavior?: ScrollBehavior) => void;
  export const scrollToRace = async (raceId?: string) => {
    // if raceId is not provided, use $selectedRaceId
    const rid = raceId || $selectedRaceId;
    // find race index
    const index = items.findIndex(
      (i) => i.type === 'ITEM' && i.race.id === rid
    );
    if (index > -1) {
      scrollToIndex?.(index > 0 ? index - 1 : 0, 'auto');
    }
  };
  _scrollToRace.set(scrollToRace);

  // watch the current race filters and if they are cleared, scroll the current race back into view
  //   $: $raceFilters &&
  //     raceFilters.isEmpty() &&
  //     setTimeout(() => {
  //       $selectedRaceId && scrollToRace($selectedRaceId);
  //     }, 500);

  //   scroll to race when sidebar opens
  $: $sidebarOpen && $selectedRaceId && scrollToRace($selectedRaceId);
  //   $: $selectedRaceId && scrollToRace($selectedRaceId);

  // calculate sticky index which are the headers
  $: stickyIndexes = items
    .map((data, i) => {
      return { index: i, data };
    })
    .filter((item) => item.data?.type === 'HEADER')
    .map((item) => item.index);
</script>

{#if $loadingRaces}
  <div class="absolute z-40 flex w-full items-center justify-center">
    <BarLoader duration="2s" color="#d5adf7" />
  </div>
{/if}

{#if items.length > 0}
  <TinyVirtualList
    itemCount={items.length}
    itemSize={40}
    height="100%"
    bind:scrollToIndex
    {stickyIndexes}
  >
    <div slot="item" let:index let:style {style}>
      {@const item = items[index]}
      {#if item?.type === 'HEADER'}
        <RaceListHeader>{item.date}</RaceListHeader>
      {:else}
        <RaceListItem race={item.race} />
      {/if}
    </div>
  </TinyVirtualList>
{:else if !$loadingRaces}
  <div
    class="flex h-full w-full flex-col items-center justify-center text-gray-600"
  >
    <!-- no races matching filter -->
    <TablerAlertSquareRounded height="24" width="24" />
    <span class="pt-1">No races matching filter</span>
    <!-- show what the filters are -->
    {#if $raceFilters}
      <div class="flex flex-col items-center py-4">
        {#if $raceFilters.types.length}
          <span class="text-xs">
            Types:
            {#each $raceFilters.types as type, i}
              {#if i > 0},
              {/if}
              {type}
            {/each}
          </span>
        {/if}
        {#if $raceFilters.meetings.length}
          <span class="text-center text-xs">
            Meetings:
            {#each $raceFilters.meetings as meeting, i}
              {#if i > 0},
              {/if}
              {meeting}
            {/each}
          </span>
        {/if}
        {#if $raceFilters.locations?.length}
          <span class="text-xs">
            Not locations:
            {#each $raceFilters.locations as location, i}
              {#if i > 0},
              {/if}
              {location}
            {/each}
          </span>
        {/if}
        {#if $raceFilters.dates?.length}
          <span class="text-xs">
            Dates:
            {#each $raceFilters.dates as date, i}
              {#if i > 0},
              {/if}
              {date}
            {/each}
          </span>
        {/if}
      </div>
    {/if}

    <Button on:click={() => raceFilters.clearFilters()}>Clear Filters</Button>
  </div>
{:else}
  <div class="flex h-full flex-col items-center justify-center">
    <ScaleOut />
  </div>
{/if}
